import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Image, 
  DollarSign, 
  TrendingUp,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingArtworks, setPendingArtworks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, pendingRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/pending`),
        axios.get(`${API}/admin/users`)
      ]);
      setStats(statsRes.data);
      setPendingArtworks(pendingRes.data);
      setUsers(usersRes.data.users);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const handleApprove = async (artworkId) => {
    try {
      await axios.post(`${API}/admin/approve/${artworkId}`);
      setPendingArtworks(prev => prev.filter(a => a.id !== artworkId));
      toast.success('Artwork approved');
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (artworkId) => {
    try {
      await axios.post(`${API}/admin/reject/${artworkId}`);
      setPendingArtworks(prev => prev.filter(a => a.id !== artworkId));
      toast.success('Artwork rejected');
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-32 skeleton" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 lg:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Admin</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#F5F5F0]">
            Dashboard
          </h1>
        </div>

        {/* Tabs */}
        <div className="tab-list">
          <button 
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button 
            className={`tab-item ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
            data-testid="tab-pending"
          >
            Pending ({pendingArtworks.length})
          </button>
          <button 
            className={`tab-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="tab-users"
          >
            Users
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <Image className="text-[#D4AF37]" size={24} />
                  <span className="text-[#71717A] text-sm uppercase tracking-wider">Artworks</span>
                </div>
                <div className="stat-value">{stats.total_artworks}</div>
              </div>
              
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="text-[#D4AF37]" size={24} />
                  <span className="text-[#71717A] text-sm uppercase tracking-wider">Artists</span>
                </div>
                <div className="stat-value">{stats.total_artists}</div>
              </div>
              
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="text-[#D4AF37]" size={24} />
                  <span className="text-[#71717A] text-sm uppercase tracking-wider">Members</span>
                </div>
                <div className="stat-value">{stats.total_members}</div>
              </div>
              
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="text-[#D4AF37]" size={24} />
                  <span className="text-[#71717A] text-sm uppercase tracking-wider">Revenue</span>
                </div>
                <div className="stat-value">${stats.total_platform_revenue?.toFixed(2)}</div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="p-6 bg-[#121212] border border-white/10">
                <h3 className="text-[#71717A] text-sm uppercase tracking-wider mb-4">Listing Fees</h3>
                <p className="font-serif text-3xl text-[#F5F5F0]">${stats.total_listing_fees?.toFixed(2)}</p>
              </div>
              
              <div className="p-6 bg-[#121212] border border-white/10">
                <h3 className="text-[#71717A] text-sm uppercase tracking-wider mb-4">Commissions (18%)</h3>
                <p className="font-serif text-3xl text-[#F5F5F0]">${stats.total_commission_earned?.toFixed(2)}</p>
              </div>
              
              <div className="p-6 bg-[#121212] border border-white/10">
                <h3 className="text-[#71717A] text-sm uppercase tracking-wider mb-4">Memberships</h3>
                <p className="font-serif text-3xl text-[#F5F5F0]">${stats.total_membership_fees?.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div>
            {pendingArtworks.length === 0 ? (
              <div className="text-center py-24">
                <CheckCircle className="mx-auto text-[#10B981] mb-6" size={48} />
                <p className="text-[#71717A]">No pending artworks to review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingArtworks.map(artwork => (
                  <div
                    key={artwork.id}
                    className="flex flex-col sm:flex-row gap-4 p-4 bg-[#121212] border border-white/10"
                    data-testid={`pending-${artwork.id}`}
                  >
                    <img
                      src={artwork.image_url}
                      alt={artwork.title}
                      className="w-full sm:w-32 h-32 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-serif text-lg text-[#F5F5F0]">{artwork.title}</h3>
                      <p className="text-[#71717A] text-sm">by {artwork.artist_name}</p>
                      <p className="text-[#A1A1AA] text-sm mt-2 line-clamp-2">{artwork.description}</p>
                      {artwork.moderation_reason && (
                        <p className="text-[#EF4444] text-xs mt-2">
                          Moderation note: {artwork.moderation_reason}
                        </p>
                      )}
                    </div>
                    <div className="flex sm:flex-col gap-2">
                      <button
                        onClick={() => handleApprove(artwork.id)}
                        className="flex-1 sm:flex-none p-2 bg-[#10B981] text-white hover:bg-[#10B981]/80 transition-colors flex items-center justify-center gap-2"
                        data-testid={`approve-${artwork.id}`}
                      >
                        <CheckCircle size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(artwork.id)}
                        className="flex-1 sm:flex-none p-2 bg-[#EF4444] text-white hover:bg-[#EF4444]/80 transition-colors flex items-center justify-center gap-2"
                        data-testid={`reject-${artwork.id}`}
                      >
                        <XCircle size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Member</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} data-testid={`user-${user.id}`}>
                    <td className="text-[#F5F5F0]">{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${
                        user.role === 'admin' ? 'badge-gold' : (
                        user.role === 'artist' ? 'badge-success' : '')
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      {user.is_member ? (
                        <CheckCircle className="text-[#10B981]" size={18} />
                      ) : (
                        <XCircle className="text-[#71717A]" size={18} />
                      )}
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
